import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { SortingState } from '@tanstack/react-table';
import { useDebounced } from './useDebounced';

/*
 * Page state in the query string, schema-driven (the generalisation of
 * `features/charts/chartsUrlState.ts`). A schema is a plain object of codecs; its key order is the
 * order of the written query. The URL carries only what differs from the defaults, and a value that
 * fails its codec parses as the default, so the next write drops it.
 */

/** One URL parameter: how to read it (always valid, falling back to the default) and how to write it. */
export interface Codec<T> {
  default: () => T;
  /** The value of the parameter, or null when it is absent; an invalid value is the default. */
  parse: (raw: string | null) => T;
  /** The text written for a value; '' is never written (the parameter is absent). */
  format: (value: T) => string;
}

// `any`: a schema mixes codecs of different value types, and `Codec<T>` is invariant in `T`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Schema = Record<string, Codec<any>>;
export type UrlState<S extends Schema> = { [K in keyof S]: S[K] extends Codec<infer T> ? T : never };

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;
const INT_RE = /^-?\d+$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Free text, as typed. */
export const text = (): Codec<string> => ({
  default: () => '',
  parse: (raw) => raw ?? '',
  format: (value) => value,
});

/** The options of a `oneOf` filter `Select`: "any" first, then each key of `labels` with its label, in the record's order. */
export function optionsOf<T extends string>(labels: Record<T, string>, anyLabel: string): { value: T | ''; label: string }[] {
  return [{ value: '', label: anyLabel }, ...(Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label }))];
}

/** One key of `set` (any `Record<E, …>`: with `E` a schema enum the compiler flags a value added to the schema but not here), else `fallback`; '' means "any". */
export function oneOf<T extends string, D extends T | ''>(set: Record<T, unknown>, fallback: D): Codec<T | D> {
  return {
    default: () => fallback,
    parse: (raw) => (raw !== null && Object.hasOwn(set, raw) ? (raw as T) : fallback),
    format: (value) => value,
  };
}

/** A `yesNo` value as the `'yes' | 'no' | ''` text of the URL and of a filter `Select`, and back: the one mapping. */
export const yesNoToOption = (value: boolean | null): 'yes' | 'no' | '' => (value === null ? '' : value ? 'yes' : 'no');
export const optionToYesNo = (option: string): boolean | null => (option === 'yes' ? true : option === 'no' ? false : null);

/** `yes` / `no` as true / false; absent (any) is null. */
export const yesNo = (): Codec<boolean | null> => ({
  default: () => null,
  parse: (raw) => optionToYesNo(raw ?? ''),
  format: yesNoToOption,
});

/** An integer within the bounds, else `fallback`. */
export const int = (bounds: { min?: number; max?: number }, fallback: number): Codec<number> => ({
  default: () => fallback,
  parse: (raw) => {
    if (raw === null || !INT_RE.test(raw)) return fallback;
    const n = Number(raw);
    if (!Number.isSafeInteger(n)) return fallback;
    if (bounds.min !== undefined && n < bounds.min) return fallback;
    if (bounds.max !== undefined && n > bounds.max) return fallback;
    return n;
  },
  format: (value) => String(value),
});

/** An integer within the bounds kept as typed (`'40'`), '' when absent or out of range; the page converts it where it needs a number. */
export const intText = (bounds: { min: number; max: number }): Codec<string> => ({
  default: () => '',
  parse: (raw) => {
    if (raw === null || !INT_RE.test(raw)) return '';
    const n = Number(raw);
    return n >= bounds.min && n <= bounds.max ? String(n) : '';
  },
  format: (value) => value,
});

/** The Active-status select of a list: only active rows, only inactive ones, or all (`show=` in the URL). */
export type ActiveVisibility = 'active' | 'inactive' | 'all';
export const ACTIVE_VISIBILITY_VALUES: Record<ActiveVisibility, true> = { active: true, inactive: true, all: true };

/** `show=active|inactive|all`; `fallback` is the page's default, left out of the URL. */
export const activeVisibility = (fallback: ActiveVisibility): Codec<ActiveVisibility> => oneOf(ACTIVE_VISIBILITY_VALUES, fallback);

/** A decimal number kept as typed (`12.5`, `-3`), '' when absent; the page converts it where it needs a number. */
export const decimal = (): Codec<string> => ({
  default: () => '',
  parse: (raw) => (raw !== null && DECIMAL_RE.test(raw) ? raw : ''),
  format: (value) => value,
});

/** `=1` is true; anything else, or absent, is false. */
export const flag = (): Codec<boolean> => ({
  default: () => false,
  parse: (raw) => raw === '1',
  format: (value) => (value ? '1' : ''),
});

function isRealDate(value: string): boolean {
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** A calendar date `YYYY-MM-DD`; the default ('' unless given) may be a thunk, for a default that depends on today. */
export const isoDate = (fallback: string | (() => string) = ''): Codec<string> => {
  const resolve = () => (typeof fallback === 'function' ? fallback() : fallback);
  return {
    default: resolve,
    parse: (raw) => (raw !== null && isRealDate(raw) ? raw : resolve()),
    format: (value) => value,
  };
};

/** One UUID, lower-cased. */
export const uuid = (): Codec<string> => ({
  default: () => '',
  parse: (raw) => (raw !== null && UUID_RE.test(raw) ? raw.toLowerCase() : ''),
  format: (value) => value,
});

/** Comma-separated UUIDs, lower-cased and de-duplicated; an element that is not a UUID is dropped. */
export const uuidList = (): Codec<string[]> => ({
  default: () => [],
  parse: (raw) =>
    raw === null
      ? []
      : [...new Set(raw.split(',').map((id) => id.trim().toLowerCase()).filter((id) => UUID_RE.test(id)))],
  format: (value) => value.join(','),
});

/** The state of an empty query string. */
export function defaultsOf<S extends Schema>(schema: S): UrlState<S> {
  return Object.fromEntries(Object.entries(schema).map(([key, codec]) => [key, codec.default()])) as UrlState<S>;
}

/**
 * The state a query string describes. `aliases` maps a parse-only parameter to the schema key it
 * stands for (`{ discountId: 'discounts' }`): the real key wins when both are present, and the alias
 * is never written back.
 */
export function parseUrlState<S extends Schema>(
  schema: S,
  params: URLSearchParams,
  options: { aliases?: Record<string, string> } = {},
): UrlState<S> {
  const aliases = Object.entries(options.aliases ?? {});
  const out: Record<string, unknown> = {};
  for (const [key, codec] of Object.entries(schema)) {
    let raw = params.get(key);
    if (raw === null) {
      for (const [alias, target] of aliases) {
        if (target === key && params.has(alias)) raw = params.get(alias);
      }
    }
    out[key] = codec.parse(raw);
  }
  return out as UrlState<S>;
}

/** The text a state value is written as, or null when it is the default (or empty) and so is left out. */
function written<T>(codec: Codec<T>, value: T): string | null {
  const text = codec.format(value);
  return text !== '' && text !== codec.format(codec.default()) ? text : null;
}

/** Whether `key` holds its default in `state` (what `serializeUrlState` would leave out). */
export function isDefault<S extends Schema>(schema: S, state: UrlState<S>, key: keyof S): boolean {
  return written(schema[key], state[key]) === null;
}

/**
 * The query string (no leading `?`) for a state: only non-defaults, in schema order. Commas and
 * colons stay literal so the link reads `devices=<a>,<b>&from=22:40`.
 */
export function serializeUrlState<S extends Schema>(schema: S, state: UrlState<S>): string {
  const out = new URLSearchParams();
  for (const [key, codec] of Object.entries(schema)) {
    const text = written(codec, (state as Record<string, unknown>)[key]);
    if (text !== null) out.set(key, text);
  }
  return out.toString().replace(/%2C/gi, ',').replace(/%3A/gi, ':');
}

/** What `patch` takes: the fields to change, or a function from the current state to them. */
export type UrlPatch<S extends Schema> = Partial<UrlState<S>> | ((current: UrlState<S>) => Partial<UrlState<S>>);

export interface UrlStateApi<S extends Schema> {
  state: UrlState<S>;
  /** Merge changes into the URL, keeping the page number. */
  patch: (change: UrlPatch<S>) => void;
  /** `patch` for anything that changes which rows match (a filter, the sort, the page size): the page number goes back to its default. */
  setFilters: (change: UrlPatch<S>) => void;
  /** Clear the whole query string. */
  reset: () => void;
}

/**
 * The page state held in the query string. `patch` merges changes into the current state and
 * rewrites the URL in place (`replace`: one history entry however many controls are used, so the
 * browser Back from a detail returns to the grid as it was). The base of a merge is the URL as it
 * is right now (`window.location`, which the replace updates synchronously), not the last render:
 * the router commits a location change in a transition, so two controls changed within one frame
 * would otherwise merge onto stale state and drop the first change. A change that derives from the
 * current value passes a function, which receives that fresh state. Parameters the schema does not
 * know are not carried over.
 */
export function useUrlState<S extends Schema>(
  schema: S,
  options: { aliases?: Record<string, string> } = {},
): UrlStateApi<S> {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Pass `aliases` as a module constant: a new object each render would re-create `state` and `patch`.
  const { aliases } = options;
  const state = useMemo(() => parseUrlState(schema, params, { aliases }), [schema, params, aliases]);

  const patch = useCallback(
    (change: UrlPatch<S>) => {
      const current = parseUrlState(schema, new URLSearchParams(window.location.search), { aliases });
      const partial = typeof change === 'function' ? change(current) : change;
      navigate({ search: serializeUrlState(schema, { ...current, ...partial }) }, { replace: true });
    },
    [navigate, schema, aliases],
  );

  const setFilters = useCallback(
    (change: UrlPatch<S>) => {
      patch((current) => ({
        ...(typeof change === 'function' ? change(current) : change),
        ...('page' in schema ? { page: schema.page.default() } : {}),
      }));
    },
    [patch, schema],
  );

  const reset = useCallback(() => navigate({ search: '' }, { replace: true }), [navigate]);

  return { state, patch, setFilters, reset };
}

/**
 * A text box whose value lives in the URL. Typing shows at once (the draft) and writes the URL once
 * per pause (`commit` after `ms`), so a search asks Go once per pause, not per key. A value that
 * arrives from outside (browser Back, an inbound link, Reset) replaces the draft and cancels the
 * pending write of the older draft; the URL catching up with what was just committed does not.
 */
export function useDebouncedUrlField(
  value: string,
  commit: (next: string) => void,
  ms = 250,
): [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  const debounced = useDebounced(draft, ms);
  const settled = useRef(value);
  const commitRef = useRef(commit);

  useEffect(() => {
    commitRef.current = commit;
  });

  useEffect(() => {
    if (value === settled.current) return;
    settled.current = value;
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (debounced === settled.current) return;
    settled.current = debounced;
    commitRef.current(debounced);
  }, [debounced]);

  return [draft, setDraft];
}

/** The URL's `sort` / `dir` as a TanStack sorting state. */
export function sortingFromUrl(url: { sort: string; dir: string }): SortingState {
  return [{ id: url.sort, desc: url.dir === 'desc' }];
}

/** A TanStack sorting state as `sort` / `dir`; no sorting (the third click on a header) is the page's default sort. */
export function sortingToUrl<C extends string>(
  sorting: SortingState,
  fallback: { sort: C; dir: 'asc' | 'desc' },
): { sort: C; dir: 'asc' | 'desc' } {
  const first = sorting[0];
  return first ? { sort: first.id as C, dir: first.desc ? 'desc' : 'asc' } : fallback;
}
